"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { sanitizeErrorForUser } from "@/utils/error-sanitizer";
import type { ProcessedContent } from "./media-utils";
import { buildProcessedContent } from "./media-utils";
import { DROP_DETAIL_STALE_TIME_MS, fetchDropByIdBatched, getDropQueryKey } from "@/services/api/drop-api";
interface UseDropContentResult {
  drop: ApiDrop | null;
  content: ProcessedContent;
  isLoading: boolean;
  error: unknown;
}

/**
 * Custom hook to fetch and process drop content
 */
export const useDropContent = (dropId, dropPartId, maybeDrop) => {
  const $ = _c(32);
  let t0;
  if ($[0] !== dropId) {
    t0 = getDropQueryKey(dropId);
    $[0] = dropId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== dropId) {
    t1 = () => fetchDropByIdBatched(dropId);
    $[2] = dropId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = maybeDrop ?? undefined;
  const t3 = !maybeDrop;
  let t4;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t0,
      queryFn: t1,
      placeholderData: keepPreviousData,
      initialData: t2,
      enabled: t3,
      staleTime: DROP_DETAIL_STALE_TIME_MS
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data: drop,
    isFetching,
    error
  } = useQuery(t4);
  let t5;
  if ($[9] !== drop || $[10] !== dropPartId || $[11] !== error || $[12] !== isFetching) {
    bb0: {
      if (isFetching && !drop) {
        let t6;
        if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = {
            segments: [{
              type: "text",
              content: "Loading..."
            }],
            apiMedia: []
          };
          $[14] = t6;
        } else {
          t6 = $[14];
        }
        t5 = t6;
        break bb0;
      }
      if (error) {
        let t6;
        if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = /Drop [0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12} not found/;
          $[15] = t6;
        } else {
          t6 = $[15];
        }
        const regex = t6;
        let t7;
        if ($[16] !== error) {
          const sanitizedError = sanitizeErrorForUser(error);
          t7 = regex.test(sanitizedError) ? "This drop has been deleted by the author" : sanitizedError;
          $[16] = error;
          $[17] = t7;
        } else {
          t7 = $[17];
        }
        const errorMsg = t7;
        let t8;
        if ($[18] !== errorMsg) {
          t8 = [{
            type: "text",
            content: errorMsg
          }];
          $[18] = errorMsg;
          $[19] = t8;
        } else {
          t8 = $[19];
        }
        let t9;
        if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = [];
          $[20] = t9;
        } else {
          t9 = $[20];
        }
        let t10;
        if ($[21] !== t8) {
          t10 = {
            segments: t8,
            apiMedia: t9
          };
          $[21] = t8;
          $[22] = t10;
        } else {
          t10 = $[22];
        }
        t5 = t10;
        break bb0;
      }
      if (!drop) {
        let t6;
        if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = {
            segments: [],
            apiMedia: []
          };
          $[23] = t6;
        } else {
          t6 = $[23];
        }
        t5 = t6;
        break bb0;
      }
      let t6;
      if ($[24] !== dropPartId) {
        t6 = p => p.part_id === dropPartId;
        $[24] = dropPartId;
        $[25] = t6;
      } else {
        t6 = $[25];
      }
      const part = drop.parts.find(t6);
      if (!part) {
        let t7;
        if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = {
            segments: [],
            apiMedia: []
          };
          $[26] = t7;
        } else {
          t7 = $[26];
        }
        t5 = t7;
        break bb0;
      }
      t5 = buildProcessedContent(part.content, part.media);
    }
    $[9] = drop;
    $[10] = dropPartId;
    $[11] = error;
    $[12] = isFetching;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const content = t5;
  const t6 = drop ?? null;
  const t7 = isFetching && !maybeDrop;
  let t8;
  if ($[27] !== content || $[28] !== error || $[29] !== t6 || $[30] !== t7) {
    t8 = {
      drop: t6,
      content,
      isLoading: t7,
      error
    };
    $[27] = content;
    $[28] = error;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  return t8;
};